import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { getJson } from "../../../utils/api";
import {
  Curriculum,
  AdminFunnelLessonItem,
  AdminFunnelUnitItem,
} from "../../../models";
import { ADMIN, fmtPct } from "../utils";

interface UnitRow {
  unit_id: string;
  unit_title: string;
  order_index: number;
  total_lessons: number;
  total_reached: number;
  total_completed: number;
  completion_pct: number | null;
  lessons: AdminFunnelLessonItem[];
}
type UnitSortKey =
  | "order_index"
  | "unit_title"
  | "total_lessons"
  | "total_reached"
  | "total_completed"
  | "completion_pct";
type SortDir = "asc" | "desc";

const UnitProgressTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unitRows, setUnitRows] = useState<UnitRow[]>([]);
  const [expandedUnitId, setExpandedUnitId] = useState<string | null>(null);

  // Curriculum selector
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<string>("");

  // Client-side sort
  const [sortKey, setSortKey] = useState<UnitSortKey>("order_index");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Fetch curricula list on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getJson(`${ADMIN}/curricula`);
        const { data } = await resp.json();
        if (cancelled) return;
        const list: Curriculum[] = data ?? [];
        setCurricula(list);
        if (list.length > 0) {
          setSelectedCurriculumId(list[0].id);
        }
      } catch (e) {
        console.error("Failed to fetch curricula", e);
        if (!cancelled) setError("Failed to load curricula");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch funnel data when curriculum changes
  useEffect(() => {
    if (!selectedCurriculumId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await getJson(`${ADMIN}/learning/metrics/funnel`);
        const { data } = await resp.json();
        if (cancelled) return;
        const units: AdminFunnelUnitItem[] = data?.units ?? [];
        const aggregated: UnitRow[] = units.map((unit, idx) => {
          const totalReached = unit.lessons.reduce(
            (s, l) => s + l.users_reached,
            0
          );
          const totalCompleted = unit.lessons.reduce(
            (s, l) => s + l.users_completed,
            0
          );
          return {
            unit_id: unit.unit_id,
            unit_title: unit.unit_title,
            order_index: idx,
            total_lessons: unit.lessons.length,
            total_reached: totalReached,
            total_completed: totalCompleted,
            completion_pct:
              totalReached > 0
                ? Math.round((totalCompleted / totalReached) * 1000) / 10
                : null,
            lessons: unit.lessons,
          };
        });
        setUnitRows(aggregated);
        setExpandedUnitId(null);
      } catch (e) {
        console.error("Failed to fetch funnel data", e);
        if (!cancelled) setError("Failed to load unit progress data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCurriculumId]);

  // Sorted data
  const sortedUnits = useMemo(() => {
    return [...unitRows].sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [unitRows, sortKey, sortDir]);

  const handleSort = (key: UnitSortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const mkSortLabel = (
    label: string,
    key: UnitSortKey,
    align?: "right",
    tooltip?: string
  ) => (
    <TableCell align={align}>
      <Tooltip
        title={tooltip ?? ""}
        arrow
        placement="top"
        disableHoverListener={!tooltip}
      >
        <TableSortLabel
          active={sortKey === key}
          direction={sortKey === key ? sortDir : "asc"}
          onClick={() => handleSort(key)}
        >
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, mt: 1 }}>
        <Typography variant="h6">Unit Progress</Typography>
        {curricula.length > 0 && (
          <TextField
            select
            size="small"
            label="Curriculum"
            value={selectedCurriculumId}
            onChange={(e) => setSelectedCurriculumId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {curricula.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.title}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 32 }} />
                {mkSortLabel("Unit", "unit_title")}
                {mkSortLabel(
                  "Lessons",
                  "total_lessons",
                  "right",
                  "Number of lessons in this unit"
                )}
                {mkSortLabel(
                  "Users Reached",
                  "total_reached",
                  "right",
                  "Sum of users who have a non-locked status for lessons in this unit"
                )}
                {mkSortLabel(
                  "Users Completed",
                  "total_completed",
                  "right",
                  "Sum of users who completed lessons in this unit"
                )}
                {mkSortLabel(
                  "Completion %",
                  "completion_pct",
                  "right",
                  "Users Completed / Users Reached"
                )}
                <TableCell align="center">CMS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUnits.map((unit) => (
                <React.Fragment key={unit.unit_id}>
                  <TableRow
                    hover
                    onClick={() =>
                      setExpandedUnitId(
                        expandedUnitId === unit.unit_id ? null : unit.unit_id
                      )
                    }
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ px: 0.5 }}>
                      <IconButton size="small">
                        {expandedUnitId === unit.unit_id ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>{unit.unit_title}</TableCell>
                    <TableCell align="right">{unit.total_lessons}</TableCell>
                    <TableCell align="right">{unit.total_reached}</TableCell>
                    <TableCell align="right">{unit.total_completed}</TableCell>
                    <TableCell align="right">
                      {fmtPct(unit.completion_pct)}
                    </TableCell>
                    <TableCell
                      align="center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MuiLink
                        component={Link}
                        to={`/curricula/${selectedCurriculumId}`}
                        underline="hover"
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </MuiLink>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: "none" }}>
                      <Collapse
                        in={expandedUnitId === unit.unit_id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ py: 1, px: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Lessons in {unit.unit_title}
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Lesson</TableCell>
                                <TableCell>Type</TableCell>
                                <Tooltip
                                  title="Users who have a non-locked status for this lesson"
                                  arrow
                                  placement="top"
                                >
                                  <TableCell align="right">
                                    Users Reached
                                  </TableCell>
                                </Tooltip>
                                <Tooltip
                                  title="Users who completed this lesson"
                                  arrow
                                  placement="top"
                                >
                                  <TableCell align="right">
                                    Users Completed
                                  </TableCell>
                                </Tooltip>
                                <Tooltip
                                  title="Users Completed / Users Reached"
                                  arrow
                                  placement="top"
                                >
                                  <TableCell align="right">
                                    Completion %
                                  </TableCell>
                                </Tooltip>
                                <TableCell align="center">CMS</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {unit.lessons.map((lesson) => (
                                <TableRow key={lesson.lesson_id}>
                                  <TableCell>{lesson.lesson_title}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={lesson.lesson_type}
                                      variant="outlined"
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {lesson.users_reached}
                                  </TableCell>
                                  <TableCell align="right">
                                    {lesson.users_completed}
                                  </TableCell>
                                  <TableCell align="right">
                                    {lesson.users_reached > 0
                                      ? `${
                                          Math.round(
                                            (lesson.users_completed /
                                              lesson.users_reached) *
                                              1000
                                          ) / 10
                                        }%`
                                      : "--"}
                                  </TableCell>
                                  <TableCell align="center">
                                    <MuiLink
                                      component={Link}
                                      to={`/lessons/${lesson.lesson_id}/edit`}
                                      underline="hover"
                                    >
                                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                                    </MuiLink>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {sortedUnits.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No data
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default React.memo(UnitProgressTab);
